import React, { useState } from 'react';
import { Calendar, ArrowRight, AlertCircle, Zap, Activity, Coffee, Target, Layers } from 'lucide-react';
import { calculateWeeksRemaining, generateSchedule } from '../../services/scheduler';
import { INTENSITY_LABELS, STRATEGY_LABELS } from '../../constants';
import api from '../../services/api';

const INTENSITY_ICONS = {
  'CAUTIOUS': <Coffee className="w-5 h-5" />,
  'MODERATE': <Activity className="w-5 h-5" />,
  'INTENSE': <Zap className="w-5 h-5" />,
};

const STRATEGY_ICONS = {
  'COVER_ALL': <Layers className="w-5 h-5" />,
  'PRIORITIZE_HIGH': <Target className="w-5 h-5" />,
};

export const SetupScreen = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState('');
  const [weeks, setWeeks] = useState(0);
  const [selectedIntensity, setSelectedIntensity] = useState('MODERATE');
  const [selectedStrategy, setSelectedStrategy] = useState('PRIORITIZE_HIGH');
  const [error, setError] = useState('');

  // Data atual no formato YYYY-MM-DD para o atributo min do input
  const today = new Date().toISOString().split('T')[0];

  const handleDateSubmit = (e) => {
    e.preventDefault();
    if (!date) {
      setError('Por favor, selecione uma data.');
      return;
    }

    const calculatedWeeks = calculateWeeksRemaining(date);
    
    if (calculatedWeeks <= 0) {
      setError('A data selecionada deve ser no futuro (pelo menos 1 semana).');
      return;
    }
    
    setWeeks(calculatedWeeks);
    setError('');
    setStep(2);
  };

  const [isGenerating, setIsGenerating] = useState(false);

  const handleFinalSubmit = async () => {
    setIsGenerating(true);
    try {
      // Gerar o cronograma antes de completar
      const generatedSchedule = generateSchedule(weeks, selectedIntensity, selectedStrategy);

      // Salvar no backend
      try {
        const token = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')).token : null;
        if (token) {
          const config = {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          };

          await api.post('/api/cronograma-personalizado', {
            examDate: date,
            weeksAvailable: weeks,
            intensity: selectedIntensity,
            strategy: selectedStrategy,
            semanas: generatedSchedule
          }, config);
        }
      } catch (error) {
        console.error('Erro ao salvar cronograma:', error);
      }

      onComplete(date, weeks, selectedIntensity, selectedStrategy);
    } catch (error) {
      console.error('Erro ao gerar cronograma:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const getStepTitle = () => {
    if (step === 1) return "Vamos organizar sua rotina de estudos com base na data da prova.";
    if (step === 2) return "Qual será a intensidade dos seus estudos?";
    return "Qual sua estratégia de aprendizado?";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-fuchsia-50 px-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-lg transition-all duration-300">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-violet-800" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Planejador ENEM</h1>
          <p className="text-gray-600">{getStepTitle()}</p>
        </div>

        {step === 1 && (
          <form onSubmit={handleDateSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quando será a prova?
              </label>
              <input
                type="date"
                min={today}
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setError('');
                }}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors bg-white text-gray-900 placeholder-gray-500 shadow-sm"
                style={{ colorScheme: 'light' }}
              />
              {error && (
                <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            <div className="bg-violet-50 p-4 rounded-lg text-sm text-violet-900">
              <p>
                O sistema irá calcular automaticamente as semanas disponíveis até o exame.
              </p>
            </div>

            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 bg-violet-800 hover:bg-violet-900 text-white font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-[1.02]"
            >
              Continuar
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="space-y-3">
              {Object.keys(INTENSITY_LABELS).map((intensity) => {
                const isSelected = selectedIntensity === intensity;
                return (
                  <button
                    key={intensity}
                    onClick={() => setSelectedIntensity(intensity)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-violet-800 bg-violet-50 text-violet-900 shadow-md'
                        : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-violet-200 hover:bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-violet-200 text-violet-900' : 'bg-gray-200 text-gray-500'}`}>
                        {INTENSITY_ICONS[intensity]}
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{INTENSITY_LABELS[intensity].label}</p>
                      </div>
                    </div>
                    {isSelected && <div className="w-3 h-3 bg-violet-800 rounded-full" />}
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 px-6 rounded-lg text-gray-600 font-semibold hover:bg-gray-100 transition-colors"
              >
                Voltar
              </button>
              <button
                onClick={() => setStep(3)}
                className="flex-1 bg-violet-800 hover:bg-violet-900 text-white font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-[1.02]"
              >
                Continuar
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="space-y-3">
              {Object.keys(STRATEGY_LABELS).map((strategy) => {
                const isSelected = selectedStrategy === strategy;
                return (
                  <button
                    key={strategy}
                    onClick={() => setSelectedStrategy(strategy)}
                    className={`w-full flex flex-col p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-violet-800 bg-violet-50 text-violet-900 shadow-md'
                        : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-violet-200 hover:bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between w-full mb-1">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isSelected ? 'bg-violet-200 text-violet-900' : 'bg-gray-200 text-gray-500'}`}>
                          {STRATEGY_ICONS[strategy]}
                        </div>
                        <p className="font-medium">{STRATEGY_LABELS[strategy].label}</p>
                      </div>
                      {isSelected && <div className="w-3 h-3 bg-violet-800 rounded-full" />}
                    </div>
                    <p className={`text-sm ml-[3.25rem] ${isSelected ? 'text-violet-700' : 'text-gray-400'}`}>
                      {STRATEGY_LABELS[strategy].desc}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setStep(2)}
                className="flex-1 py-3 px-6 rounded-lg text-gray-600 font-semibold hover:bg-gray-100 transition-colors"
                disabled={isGenerating}
              >
                Voltar
              </button>
              <button
                onClick={handleFinalSubmit}
                className="flex-1 bg-violet-800 hover:bg-violet-900 text-white font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-[1.02]"
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Gerando...
                  </div>
                ) : (
                  'Gerar Cronograma'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};